import { useEffect, useRef, useState, ReactNode } from "react";
import { authClient, useSession } from "@/lib/auth-client";
import { AuthContext, type Session, type User } from "@/contexts/auth-context";
type MagicLinkResponse = { error?: { message?: string } | null } | null | undefined;
type SignInWithMagicLink = {
  magicLink: (args: { email: string; callbackURL: string }) => Promise<MagicLinkResponse>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { data: sessionData, isPending } = useSession();
  const [verifyingOtt, setVerifyingOtt] = useState(false);
  const ottVerified = useRef(false);

  // Exchange one-time token from magic link redirect for a session.
  // The crossDomain server plugin appends ?ott=<token> to the redirect URL.
  useEffect(() => {
    if (ottVerified.current) return;
    const params = new URLSearchParams(window.location.search);
    const ott = params.get("ott");
    if (!ott) return;

    ottVerified.current = true;
    setVerifyingOtt(true);

    authClient.crossDomain.oneTimeToken
      .verify({ token: ott })
      .then(() => {
        // Clean the OTT from the URL
        params.delete("ott");
        const clean = params.toString();
        const newUrl = window.location.pathname + (clean ? `?${clean}` : "");
        window.history.replaceState({}, "", newUrl);
      })
      .catch((err: unknown) => {
        console.error("OTT verification failed:", err);
      })
      .finally(() => {
        setVerifyingOtt(false);
      });
  }, []);

  const signInWithMagicLink = async (email: string, callbackURL?: string): Promise<{ error: Error | null }> => {
    try {
      const signInClient = authClient.signIn as unknown as SignInWithMagicLink;
      const result = await signInClient.magicLink({
        email,
        callbackURL: callbackURL || window.location.origin + "/portal",
      });
      if (result?.error) {
        return { error: new Error(result.error.message || "Failed to send magic link") };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const handleSignOut = async () => {
    await authClient.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user: (sessionData?.user as unknown) as User | null,
        session: sessionData?.session as Session | null,
        loading: isPending || verifyingOtt,
        signInWithMagicLink,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
